import dayjs from 'dayjs';
import {
    buildScheduleExportProjection,
    ScheduleExportPosition,
    ScheduleExportSource,
} from './scheduleExportProjection';

interface PersonnelStat {
    employeeId: number;
    name: string;
    code: string;
    workDays: number;
    shiftHours: number;
    operationHours: number;
    utilization: number;
    maxConsecutiveWork: number;
    nightCount: number;
    nonWorkdayCount: number;
}

const escapeHtml = (value: unknown): string => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatDate = (value: unknown): string => {
    const parsed = dayjs(value as any);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '-';
};

const formatDateTime = (value: unknown): string => {
    const parsed = dayjs(value as any);
    return parsed.isValid() ? parsed.format('MM-DD HH:mm') : '-';
};

const isWorkShift = (shift: any): boolean => {
    const kind = String(shift.attendance_kind || shift.plan_type || '').toUpperCase();
    return kind ? kind === 'WORK' : Number(shift.nominal_hours || 0) > 0.01;
};

const calculateUnionHours = (intervals: Array<[number, number]>): number => {
    const sorted = intervals
        .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start)
        .sort((left, right) => left[0] - right[0]);
    if (!sorted.length) return 0;
    let total = 0;
    let [currentStart, currentEnd] = sorted[0];
    sorted.slice(1).forEach(([start, end]) => {
        if (start <= currentEnd) {
            currentEnd = Math.max(currentEnd, end);
        } else {
            total += currentEnd - currentStart;
            currentStart = start;
            currentEnd = end;
        }
    });
    return (total + currentEnd - currentStart) / 3_600_000;
};

const buildPersonnelStats = (
    data: ScheduleExportSource,
    positions: ScheduleExportPosition[],
): PersonnelStat[] => {
    const dates = (data.calendar_days || []).map((day) => day.date).sort();
    const nonWorkDays = new Set(
        (data.calendar_days || []).filter((day) => !day.is_workday).map((day) => day.date),
    );
    const employeeMap = new Map<number, {
        name: string;
        code: string;
        dates: Map<string, boolean>;
        shiftHours: number;
        nightCount: number;
        nonWorkdayCount: number;
        intervals: Array<[number, number]>;
    }>();
    const getEmployee = (employeeId: number, name = '', code = '') => {
        if (!employeeMap.has(employeeId)) {
            employeeMap.set(employeeId, {
                name: name || `员工 ${employeeId}`,
                code,
                dates: new Map(),
                shiftHours: 0,
                nightCount: 0,
                nonWorkdayCount: 0,
                intervals: [],
            });
        }
        const employee = employeeMap.get(employeeId)!;
        if (name) employee.name = name;
        if (code) employee.code = code;
        return employee;
    };

    (data.shift_assignments || []).forEach((shift) => {
        const employeeId = Number(shift.employee_id);
        if (!Number.isInteger(employeeId)) return;
        const employee = getEmployee(employeeId, shift.employee_name, shift.employee_code);
        const work = isWorkShift(shift);
        employee.dates.set(String(shift.date).slice(0, 10), work);
        if (!work) return;
        employee.shiftHours += Number(
            shift.credited_hours ?? shift.plan_hours ?? shift.nominal_hours ?? 0,
        );
        if (shift.is_night_shift) employee.nightCount += 1;
        if (nonWorkDays.has(String(shift.date).slice(0, 10))) employee.nonWorkdayCount += 1;
    });
    positions.filter((position) => position.assigned && position.employeeId != null)
        .forEach((position) => {
            const employee = getEmployee(
                position.employeeId!,
                position.employeeName,
                position.employeeCode,
            );
            const start = dayjs(position.plannedStart).valueOf();
            const end = dayjs(position.plannedEnd).valueOf();
            if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
                employee.intervals.push([start, end]);
            }
        });

    return Array.from(employeeMap.entries()).map(([employeeId, employee]) => {
        let currentStreak = 0;
        let maxConsecutiveWork = 0;
        dates.forEach((date) => {
            if (employee.dates.get(date)) {
                currentStreak += 1;
                maxConsecutiveWork = Math.max(maxConsecutiveWork, currentStreak);
            } else {
                currentStreak = 0;
            }
        });
        const operationHours = calculateUnionHours(employee.intervals);
        return {
            employeeId,
            name: employee.name,
            code: employee.code,
            workDays: Array.from(employee.dates.values()).filter(Boolean).length,
            shiftHours: employee.shiftHours,
            operationHours,
            utilization: employee.shiftHours > 0
                ? operationHours / employee.shiftHours * 100
                : 0,
            maxConsecutiveWork,
            nightCount: employee.nightCount,
            nonWorkdayCount: employee.nonWorkdayCount,
        };
    }).sort((left, right) => left.name.localeCompare(right.name, 'zh-CN'));
};

const tokenValue = (name: string, fallback: string): string => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
};

const reportTokens = (): string => [
    `--report-primary:${tokenValue('--wx-blue-700', 'CanvasText')}`,
    `--report-primary-soft:${tokenValue('--wx-blue-50', 'Canvas')}`,
    `--report-success:${tokenValue('--wx-green-700', 'CanvasText')}`,
    `--report-success-soft:${tokenValue('--wx-green-100', 'Canvas')}`,
    `--report-warning:${tokenValue('--wx-amber-700', 'CanvasText')}`,
    `--report-warning-soft:${tokenValue('--wx-amber-50', 'Canvas')}`,
    `--report-danger:${tokenValue('--wx-red-700', 'CanvasText')}`,
    `--report-danger-soft:${tokenValue('--wx-red-50', 'Canvas')}`,
    `--report-text:${tokenValue('--wx-fg-1', 'CanvasText')}`,
    `--report-muted:${tokenValue('--wx-fg-3', 'GrayText')}`,
    `--report-border:${tokenValue('--wx-border-default', 'GrayText')}`,
    `--report-surface:${tokenValue('--wx-surface-1', 'Canvas')}`,
].join(';');

const buildCalendarSection = (data: ScheduleExportSource): string => {
    const days = [...(data.calendar_days || [])].sort((left, right) => left.date.localeCompare(right.date));
    const shiftsByEmployee = new Map<number, {
        name: string;
        code: string;
        byDate: Map<string, any>;
    }>();
    (data.shift_assignments || []).forEach((shift) => {
        const employeeId = Number(shift.employee_id);
        if (!shiftsByEmployee.has(employeeId)) {
            shiftsByEmployee.set(employeeId, {
                name: shift.employee_name || `员工 ${employeeId}`,
                code: shift.employee_code || '',
                byDate: new Map(),
            });
        }
        shiftsByEmployee.get(employeeId)!.byDate.set(String(shift.date).slice(0, 10), shift);
    });
    if (!days.length || !shiftsByEmployee.size) {
        return '<p class="empty">暂无排班日历数据</p>';
    }

    const chunks: string[] = [];
    const chunkSize = 14;
    for (let start = 0; start < days.length; start += chunkSize) {
        const chunk = days.slice(start, start + chunkSize);
        const headers = chunk.map((day) => (
            `<th class="${day.is_workday ? '' : 'non-workday'}">`
            + `${escapeHtml(dayjs(day.date).format('M/D'))}<small>${escapeHtml(dayjs(day.date).format('dd'))}</small>`
            + '</th>'
        )).join('');
        const rows = Array.from(shiftsByEmployee.values()).map((employee) => {
            const cells = chunk.map((day) => {
                const shift = employee.byDate.get(day.date);
                if (!shift) return '<td class="shift-empty">-</td>';
                const tone = !isWorkShift(shift)
                    ? 'rest'
                    : shift.is_night_shift
                        ? 'night'
                        : 'work';
                return `<td class="shift-${tone}">${escapeHtml(shift.shift_name || shift.shift_code || '-')}</td>`;
            }).join('');
            return `<tr><td class="employee">${escapeHtml(employee.name)}<small>${escapeHtml(employee.code)}</small></td>${cells}</tr>`;
        }).join('');
        chunks.push(`
            <table class="calendar-table">
                <thead><tr><th>员工</th>${headers}</tr></thead>
                <tbody>${rows}</tbody>
            </table>
        `);
    }
    return chunks.join('');
};

const buildOperationsSection = (positions: ScheduleExportPosition[]): string => {
    const byDate = new Map<string, ScheduleExportPosition[]>();
    positions.forEach((position) => {
        const date = position.rosterDate || String(position.plannedStart).slice(0, 10) || '未定日期';
        if (!byDate.has(date)) byDate.set(date, []);
        byDate.get(date)!.push(position);
    });
    if (!positions.length) return '<p class="empty">暂无操作岗位数据</p>';
    return Array.from(byDate.entries()).sort(([left], [right]) => left.localeCompare(right))
        .map(([date, items]) => `
            <section class="avoid-break">
                <h3>${escapeHtml(formatDate(date))} · ${items.length} 个岗位</h3>
                <table>
                    <thead><tr>
                        <th>批次</th><th>阶段</th><th>操作</th><th>岗位</th>
                        <th>执行时段</th><th>员工</th><th>状态</th>
                    </tr></thead>
                    <tbody>
                        ${items.map((position) => `
                            <tr>
                                <td>${escapeHtml(position.batchCode || '-')}</td>
                                <td>${escapeHtml(position.stageName || '-')}</td>
                                <td>${escapeHtml(position.operationName || '-')}</td>
                                <td>${position.positionNumber}</td>
                                <td>${escapeHtml(formatDateTime(position.plannedStart))}
                                    – ${escapeHtml(formatDateTime(position.plannedEnd))}</td>
                                <td>${escapeHtml(position.employeeName)}</td>
                                <td><span class="status-${position.assigned ? 'ok' : 'error'}">
                                    ${position.assigned ? '已分配' : '未分配'}
                                </span></td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </section>
        `).join('');
};

const buildStatsSection = (stats: PersonnelStat[]): string => `
    <table>
        <thead><tr>
            <th>员工</th><th>工作天数</th><th>计薪工时</th><th>操作工时</th>
            <th>利用率</th><th>最长连续</th><th>夜班</th><th>非工作日班次</th>
        </tr></thead>
        <tbody>
            ${stats.map((stat) => `
                <tr>
                    <td>${escapeHtml(stat.name)}<small>${escapeHtml(stat.code)}</small></td>
                    <td>${stat.workDays}</td>
                    <td>${stat.shiftHours.toFixed(1)}h</td>
                    <td>${stat.operationHours.toFixed(1)}h</td>
                    <td>${stat.utilization.toFixed(1)}%</td>
                    <td>${stat.maxConsecutiveWork}天</td>
                    <td>${stat.nightCount}</td>
                    <td>${stat.nonWorkdayCount}</td>
                </tr>
            `).join('')}
        </tbody>
    </table>
`;

const buildGapsSection = (gaps: ScheduleExportPosition[]): string => {
    if (!gaps.length) return '<p class="all-covered">所有岗位均已覆盖，无缺口。</p>';
    return `
        <div class="gap-summary">共 ${gaps.length} 个未覆盖岗位</div>
        <table>
            <thead><tr>
                <th>日期</th><th>批次</th><th>阶段</th><th>操作</th><th>岗位</th><th>资质要求</th>
            </tr></thead>
            <tbody>
                ${gaps.map((gap) => `
                    <tr>
                        <td>${escapeHtml(formatDate(gap.plannedStart))}</td>
                        <td>${escapeHtml(gap.batchCode || '-')}</td>
                        <td>${escapeHtml(gap.stageName || '-')}</td>
                        <td>${escapeHtml(gap.operationName || '-')}</td>
                        <td>${gap.positionNumber}</td>
                        <td>${escapeHtml(gap.qualifications)}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
};

export async function exportV4ScheduleToPdf(
    data: ScheduleExportSource,
    runId: number,
): Promise<void> {
    const projection = buildScheduleExportProjection(data);
    const stats = buildPersonnelStats(data, projection.positions);
    const calendarDays = [...(data.calendar_days || [])].sort(
        (left, right) => left.date.localeCompare(right.date),
    );
    const dateRange = calendarDays.length
        ? `${formatDate(calendarDays[0].date)} ~ ${formatDate(calendarDays[calendarDays.length - 1].date)}`
        : '-';
    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const uniqueEmployees = new Set(
        (data.shift_assignments || []).map((shift) => Number(shift.employee_id)),
    ).size;

    const iframe = document.createElement('iframe');
    iframe.className = 'v4-print-frame';
    iframe.title = `排班结果 V4 Run ${runId}`;
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) {
        iframe.remove();
        throw new Error('无法创建打印文档');
    }

    doc.open();
    doc.write(`
        <!doctype html>
        <html lang="zh-CN">
        <head>
            <meta charset="utf-8">
            <title>${escapeHtml(`排班结果_V4-${runId}`)}</title>
            <style>
                :root { ${reportTokens()} }
                * { box-sizing: border-box; }
                body {
                    margin: 0;
                    color: var(--report-text);
                    font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
                    font-size: 11px;
                    line-height: 1.45;
                }
                h1, h2, h3, p { margin: 0; }
                h1 { color: var(--report-primary); font-size: 24px; }
                h2 {
                    margin-bottom: 12px;
                    padding-left: 8px;
                    border-left: 4px solid var(--report-primary);
                    color: var(--report-primary);
                    font-size: 17px;
                }
                h3 { margin: 12px 0 6px; color: var(--report-text); font-size: 13px; }
                small { display: block; color: var(--report-muted); font-size: 9px; }
                .page { padding: 10mm; }
                .page-break { break-before: page; }
                .avoid-break { break-inside: avoid; }
                .header, .footer, .subtitle, .metrics, .metric {
                    display: flex;
                    align-items: center;
                }
                .header, .footer { justify-content: space-between; color: var(--report-muted); }
                .header { margin-bottom: 18px; padding-bottom: 6px; border-bottom: 1px solid var(--report-border); }
                .footer { margin-top: 18px; padding-top: 6px; border-top: 1px solid var(--report-border); }
                .subtitle { gap: 14px; margin: 8px 0 18px; color: var(--report-muted); }
                .metrics { gap: 10px; margin-bottom: 20px; }
                .metric {
                    min-width: 145px;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 3px;
                    padding: 10px 12px;
                    border: 1px solid var(--report-border);
                    border-radius: 6px;
                    background: var(--report-surface);
                }
                .metric strong { color: var(--report-primary); font-size: 20px; }
                table { width: 100%; margin-bottom: 14px; border-collapse: collapse; }
                th, td { padding: 5px 7px; border: 1px solid var(--report-border); text-align: left; }
                th { background: var(--report-primary); color: var(--report-primary-soft); font-weight: 600; }
                td { vertical-align: top; }
                td small, .employee small { margin-top: 2px; }
                .calendar-table { font-size: 9px; }
                .calendar-table th, .calendar-table td { padding: 4px; text-align: center; }
                .calendar-table .employee { min-width: 90px; text-align: left; }
                .calendar-table .non-workday, .shift-rest { background: var(--report-surface); color: var(--report-muted); }
                .shift-work { background: var(--report-success-soft); color: var(--report-success); }
                .shift-night { background: var(--report-danger-soft); color: var(--report-danger); }
                .shift-empty { color: var(--report-muted); }
                .status-ok, .all-covered { color: var(--report-success); font-weight: 600; }
                .status-error { color: var(--report-danger); font-weight: 600; }
                .gap-summary {
                    margin-bottom: 10px;
                    padding: 8px 10px;
                    border: 1px solid var(--report-danger);
                    border-radius: 6px;
                    background: var(--report-danger-soft);
                    color: var(--report-danger);
                    font-weight: 600;
                }
                .empty { color: var(--report-muted); }
                @media print {
                    @page { size: A4 landscape; margin: 9mm; }
                    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                    .page { padding: 0; }
                }
            </style>
        </head>
        <body>
            <section class="page">
                <div class="header"><span>V4 排班结果报告</span><span>Run #${runId}</span></div>
                <h1>排班结果报告</h1>
                <div class="subtitle">
                    <span>${escapeHtml(dateRange)}</span>
                    <span>${uniqueEmployees} 名员工</span>
                    <span>生成于 ${escapeHtml(generatedAt)}</span>
                </div>
                <div class="metrics">
                    <div class="metric"><span>岗位完成率</span><strong>${Math.round(projection.coverage.completionRate * 100)}%</strong></div>
                    <div class="metric"><span>操作覆盖率</span><strong>${Math.round(projection.coverage.coverageRate * 100)}%</strong></div>
                    <div class="metric"><span>岗位分配</span><strong>${projection.coverage.assignedPositions}/${projection.coverage.totalPositions}</strong></div>
                    <div class="metric"><span>完整覆盖操作</span><strong>${projection.coverage.completedOperations}/${projection.coverage.totalOperations}</strong></div>
                </div>
                <h2>排班日历</h2>
                ${buildCalendarSection(data)}
                <div class="footer"><span>V4 排班系统</span><span>Run #${runId}</span></div>
            </section>
            <section class="page page-break">
                <div class="header"><span>V4 排班结果报告</span><span>操作岗位</span></div>
                <h2>操作岗位明细</h2>
                ${buildOperationsSection(projection.positions)}
            </section>
            <section class="page page-break">
                <div class="header"><span>V4 排班结果报告</span><span>人员统计</span></div>
                <h2>人员统计</h2>
                ${buildStatsSection(stats)}
            </section>
            <section class="page page-break">
                <div class="header"><span>V4 排班结果报告</span><span>覆盖缺口</span></div>
                <h2>覆盖缺口</h2>
                ${buildGapsSection(projection.gaps)}
            </section>
        </body>
        </html>
    `);
    doc.close();

    const cleanup = () => iframe.remove();
    const printWindow = iframe.contentWindow;
    if (!printWindow) {
        cleanup();
        throw new Error('无法打开打印窗口');
    }
    printWindow.addEventListener('afterprint', cleanup, { once: true });
    window.setTimeout(() => {
        printWindow.focus();
        printWindow.print();
    }, 250);
    window.setTimeout(cleanup, 60_000);
}
